import { ReferralData, SendToCCPayload, DemographicsPayload, utmPayload } from '../types';
import { mapEnrollmentEthnicity } from './ethnicityUtils';

/**
 * Builds a SendToCC payload for a NEW record (no existing CC patient)
 * Includes ALL demographic fields
 */

export const CcFieldNames = {
  utm_source: 'Avacare Website - UTM Campaign Source',
  utm_medium: 'Avacare Website - UTM Campaign Medium',
  utm_campaign: 'Avacare Website - UTM Campaign Name',
  utm_term: 'Avacare Website - UTM Campaign Term',
  utm_content: 'Avacare Website - UTM Campaign Content',
  sID: 'Avacare Website - sID',
  psecID: 'Avacare Website - psecID',
  atid: 'Avacare Website - UTM ATID',
  gclid: 'Avacare Website - UTM GCLID',
  fbclid: 'Avacare Website - UTM FBCLID',
} as const;

export const buildNewRecordPayload = (
  row: ReferralData,
  statusValue: string,
  trackingParamsData?: any
): SendToCCPayload => {
  let formattedPhone: string | undefined;
  if (row.phone) {
    const digits = row.phone.replace(/\D/g, '');
    if (digits.length === 10) {
      formattedPhone = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else {
      formattedPhone = row.phone;
    }
  }
  const enrollmentEthnicityRaw = row.ethnicity;
  const mappedEthnicity = mapEnrollmentEthnicity(enrollmentEthnicityRaw);

  const demographics: DemographicsPayload = {
    firstName: row.firstName,
    lastName: row.lastName,
    dateOfBirth: row.dob,
    gender: row.gender,
    email: row.email,
    phone1: formattedPhone,
    address: {
      street: row.address,
      city: row.city,
      state: row.state,
      zipCode: row.zipcode,
      stateCode: row.stateCode,
    },
    patientId: row.patientId,
    ...(mappedEthnicity.ethnicity !== '-' && { ethnicity: mappedEthnicity.ethnicity }),
    ...(mappedEthnicity.race !== '-' && { race: mappedEthnicity.race }),
  };

  const consent = (row.secondaryConsentinfo as any) || {};
  const consentPayload = {
    emailOptInDate: null,
    isSecondaryContactMediumEmail: consent.isSecondaryContactMediumEmail,
    isSecondaryContactMediumSMS: consent.isSecondaryContactMediumSMS,
    smsOptInDate: null,
  };

  const utm: utmPayload = {
    utm_source: trackingParamsData?.utmSource,
    utm_medium: trackingParamsData?.utmMedium,
    utm_campaign: trackingParamsData?.utmCampaign,
    utm_term: trackingParamsData?.utmTerm,
    utm_content: trackingParamsData?.utmContent,
    sID: trackingParamsData?.sid,
    psecID: trackingParamsData?.psecId,
    atid: trackingParamsData?.atid,
    gclid: trackingParamsData?.gclid,
    fbclid: trackingParamsData?.fbclid,
  };
  return {
    ccId: null,
    guestRef: row.guestRef,
    enrollmentId: row.enrollmentId || null,
    leadId: row.leadId || 1,
    oldDedupStatus: row.dedupeStatus || null,
    newDedupStatus: statusValue,
    demographics,
    utm,
    consent: consentPayload,
    userId: localStorage.getItem('lma-userId') || '1',
  };
};

export const buildAutoMergePayload = (
  row: ReferralData,
  statusValue: string,
  enrichedDeduplicationData: any,
  ccPatientReferenceId: string
): SendToCCPayload => {
  const demographics: DemographicsPayload = {
    patientId: row.patientId,
  };

  if (!enrichedDeduplicationData?.firstName && row.firstName) {
    demographics.firstName = row.firstName;
  }

  if (!enrichedDeduplicationData?.lastName && row.lastName) {
    demographics.lastName = row.lastName;
  }

  if (!enrichedDeduplicationData?.dateOfBirth && row.dob) {
    demographics.dateOfBirth = row.dob;
  }

  if (!enrichedDeduplicationData?.primaryEmail?.email && row.email) {
    demographics.email = row.email;
  }

  if (!enrichedDeduplicationData?.phone1?.number && row.phone) {
    if (row.phone) {
      const digits = row.phone.replace(/\D/g, '');
      if (digits.length === 10) {
        demographics.phone1 = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else {
        demographics.phone1 = row.phone;
      }
    }
  }

  const needsStreet = !enrichedDeduplicationData?.address1;
  const needsCity = !enrichedDeduplicationData?.city;
  const needsState = !enrichedDeduplicationData?.state;
  const needsStateCode = !enrichedDeduplicationData?.stateCode;
  const needsZipCode = !enrichedDeduplicationData?.zip;
  const needsGender = !enrichedDeduplicationData?.genderCode;

  if (needsStreet || needsCity || needsState || needsStateCode || needsZipCode || needsGender) {
    demographics.address = {};
    if (needsStreet && row.address) {
      demographics.address.street = row.address;
    }
    if (needsCity && row.city) {
      demographics.address.city = row.city;
    }
    if (needsState && row.state) {
      demographics.address.state = row.state;
    }
    if (needsStateCode && row.stateCode) {
      demographics.address.stateCode = row.stateCode;
    }
    if (needsZipCode && row.zipcode) {
      demographics.address.zipCode = row.zipcode;
    }
    if (needsGender && row.gender) {
      demographics.gender = row.gender;
    }
  }

  const hasCustomField = (fieldName: string): boolean => {
    if (
      !enrichedDeduplicationData?.customFields ||
      !Array.isArray(enrichedDeduplicationData.customFields)
    ) {
      return false;
    }
    return enrichedDeduplicationData.customFields.some((field: any) => field.name === fieldName);
  };

  const utm: utmPayload = {};

  if (hasCustomField(CcFieldNames.utm_source) && row.utm_source) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_source
    );
    utm.utm_source = existingField?.value
      ? `${existingField.value}, ${row.utm_source}`
      : row.utm_source;
  }
  if (hasCustomField(CcFieldNames.utm_medium) && row.utm_medium) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_medium
    );
    utm.utm_medium = existingField?.value
      ? `${existingField.value}, ${row.utm_medium}`
      : row.utm_medium;
  }
  if (hasCustomField(CcFieldNames.utm_campaign) && row.utm_campaign) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_campaign
    );
    utm.utm_campaign = existingField?.value
      ? `${existingField.value}, ${row.utm_campaign}`
      : row.utm_campaign;
  }
  if (hasCustomField(CcFieldNames.utm_term) && row.utm_term) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_term
    );
    utm.utm_term = existingField?.value ? `${existingField.value}, ${row.utm_term}` : row.utm_term;
  }
  if (hasCustomField(CcFieldNames.utm_content) && row.utm_content) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.utm_content
    );
    utm.utm_content = existingField?.value
      ? `${existingField.value}, ${row.utm_content}`
      : row.utm_content;
  }
  if (hasCustomField(CcFieldNames.sID) && row.sID) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.sID
    );
    utm.sID = existingField?.value ? `${existingField.value}, ${row.sID}` : row.sID;
  }
  if (hasCustomField(CcFieldNames.psecID) && row.psecID) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.psecID
    );
    utm.psecID = existingField?.value ? `${existingField.value}, ${row.psecID}` : row.psecID;
  }
  if (hasCustomField(CcFieldNames.atid) && row.atid) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.atid
    );
    utm.atid = existingField?.value ? `${existingField.value}, ${row.atid}` : row.atid;
  }
  if (hasCustomField(CcFieldNames.gclid) && row.gclid) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.gclid
    );
    utm.gclid = existingField?.value ? `${existingField.value}, ${row.gclid}` : row.gclid;
  }
  if (hasCustomField(CcFieldNames.fbclid) && row.fbclid) {
    const existingField = enrichedDeduplicationData.customFields.find(
      (field: any) => field.name === CcFieldNames.fbclid
    );
    utm.fbclid = existingField?.value ? `${existingField.value}, ${row.fbclid}` : row.fbclid;
  }

  const consent = (row.secondaryConsentinfo as any) || {};
  const consentPayload = {
    emailOptInDate: null,
    isSecondaryContactMediumEmail: consent.isSecondaryContactMediumEmail,
    isSecondaryContactMediumSMS: consent.isSecondaryContactMediumSMS,
    smsOptInDate: null,
  };

  return {
    ccId: ccPatientReferenceId,
    guestRef: row.guestRef,
    enrollmentId: row.enrollmentId || null,
    leadId: row.leadId || 1,
    oldDedupStatus: row.dedupeStatus || null,
    newDedupStatus: statusValue,
    demographics,
    consent: consentPayload,
    utm,
    userId: localStorage.getItem('lma-userId') || '1',
  };
};

/**
 * Builds a SendToCC payload for a MERGE record
 * Only includes fields the user selected to override
 *
 * @param row - The source referral data
 * @param ccPatientId - The existing CC patient ID to merge with
 * @param selectedFields - Array of field keys the user chose to override
 * @param statusValue - The new dedupe status value
 */
export const buildMergeRecordPayloadBuilder = (
  row: ReferralData,
  ccPatientId: string,
  selectedFields: string[],
  statusValue: string,
  trackingParamsData?: any,
  targetRecord?: any
): SendToCCPayload => {
  const demographics: DemographicsPayload = {
    patientId: row.patientId,
  };
  const utm: utmPayload = {};

  const fieldMapping: Record<string, (value: any) => void> = {
    firstName: (v) => (demographics.firstName = v),
    lastName: (v) => (demographics.lastName = v),
    dob: (v) => (demographics.dateOfBirth = v),
    dateOfBirth: (v) => (demographics.dateOfBirth = v),
    gender: (v) => (demographics.gender = v),
    email: (v) => (demographics.email = v),
    address: (v) => {
      if (!demographics.address) demographics.address = {};
      demographics.address.street = v;
    },
    city: (v) => {
      if (!demographics.address) demographics.address = {};
      demographics.address.city = v;
    },
    state: (v) => {
      if (!demographics.address) demographics.address = {};
      demographics.address.state = v;
      demographics.address.stateCode = row.stateCode;
    },
    zipcode: (v) => {
      if (!demographics.address) demographics.address = {};
      demographics.address.zipCode = v;
    },
    utm_source: (v) => (utm.utm_source = v),
    utm_medium: (v) => (utm.utm_medium = v),
    utm_campaign: (v) => (utm.utm_campaign = v),
    utm_term: (v) => (utm.utm_term = v),
    utm_content: (v) => (utm.utm_content = v),
    sID: (v) => (utm.sID = v),
    psecID: (v) => (utm.psecID = v),
    atid: (v) => (utm.atid = v),
    fbclid: (v) => (utm.fbclid = v),
    gclid: (v) => (utm.gclid = v),
  };
  // Only add fields that were selected
  selectedFields.forEach((fieldKey) => {
    if (fieldKey.startsWith('phoneNumber:')) {
      const parts = fieldKey.split(':');
      const slot = parts[1] as 'phone1' | 'phone2' | 'phone3' | 'phone4';
      const phoneValue = row.phone;
      if (phoneValue && slot) {
        const digits = phoneValue.replace(/\D/g, '');
        demographics[slot] =
          digits.length === 10
            ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
            : phoneValue;
      }
      return;
    }

    // Handle ethnicity compound field: "ethnicity:<ethnicity>|<race>"
    if (fieldKey.startsWith('ethnicity:')) {
      const payload = fieldKey.slice('ethnicity:'.length); // "<ethnicity>|<race>"
      const [ethValue, raceValue] = payload.split('|');
      if (ethValue && ethValue !== '-') demographics.ethnicity = ethValue;
      if (raceValue && raceValue !== '-') demographics.race = raceValue;
      return;
    }

    const mapper = fieldMapping[fieldKey];
    if (mapper) {
      // Map field keys to trackingParamsData API keys
      const apiKeyMap: Record<string, string> = {
        utm_source: 'utmSource',
        utm_medium: 'utmMedium',
        utm_campaign: 'utmCampaign',
        utm_term: 'utmTerm',
        utm_content: 'utmContent',
        sID: 'sid',
        psecID: 'psecId',
        atid: 'atid',
        gclid: 'gclid',
        fbclid: 'fbclid',
      };

      // Map field keys to CC custom field names
      const fieldToCcName: Record<string, string> = {
        utm_source: CcFieldNames.utm_source,
        utm_medium: CcFieldNames.utm_medium,
        utm_campaign: CcFieldNames.utm_campaign,
        utm_term: CcFieldNames.utm_term,
        utm_content: CcFieldNames.utm_content,
        sID: CcFieldNames.sID,
        psecID: CcFieldNames.psecID,
        atid: CcFieldNames.atid,
        gclid: CcFieldNames.gclid,
        fbclid: CcFieldNames.fbclid,
      };

      const apiKey = apiKeyMap[fieldKey] || fieldKey;
      const sourceValue =
        (row as any)[fieldKey] || (trackingParamsData && trackingParamsData[apiKey]);

      if (sourceValue !== undefined && sourceValue !== null) {
        const ccFieldName = fieldToCcName[fieldKey];
        // For UTM fields: append to existing CC value, skip duplicates
        if (ccFieldName && targetRecord?.customFields) {
          const existingField = targetRecord.customFields.find((f: any) => f.name === ccFieldName);
          const existingValue = existingField?.value || '';
          if (existingValue) {
            const existingValues = existingValue
              .split(',')
              .map((s: string) => s.trim().toLowerCase())
              .filter(Boolean);
            const newValues = String(sourceValue)
              .split(',')
              .map((s: string) => s.trim())
              .filter(Boolean);
            const uniqueNew = newValues.filter(
              (v: string) => !existingValues.includes(v.toLowerCase())
            );
            if (uniqueNew.length > 0) {
              mapper(`${existingValue}, ${uniqueNew.join(', ')}`);
            }
            // If all values already exist, don't set anything (skip duplicates)
          } else {
            mapper(sourceValue);
          }
        } else {
          mapper(sourceValue);
        }
      }
    }
  });

  const consent = (row.secondaryConsentinfo as any) || {};
  const consentPayload: Record<string, string | boolean | null> = {};

  if (selectedFields.includes('emailOptInDate')) {
    consentPayload.emailOptInDate = consent.emailOptInDate ?? null;
  }
  if (selectedFields.includes('isSecondaryContactMediumEmail')) {
    consentPayload.isSecondaryContactMediumEmail = consent.isSecondaryContactMediumEmail ?? null;
  }
  if (selectedFields.includes('isSecondaryContactMediumSMS')) {
    consentPayload.isSecondaryContactMediumSMS = consent.isSecondaryContactMediumSMS ?? null;
  }
  if (selectedFields.includes('smsOptInDate')) {
    consentPayload.smsOptInDate = consent.smsOptInDate ?? null;
  }

  return {
    ccId: ccPatientId,
    guestRef: row.guestRef,
    enrollmentId: row.enrollmentId || null,
    leadId: row.leadId || 1,
    oldDedupStatus: row.dedupeStatus || null,
    newDedupStatus: statusValue,
    demographics,
    consent: consentPayload,
    utm,
    userId: localStorage.getItem('lma-userId') || '1',
  };
};
